/**
 * Deterministic project ids for sync.
 *
 * A local sketch has one id (its room). The relational schema needs a PROJECT
 * id too, and two spaces in the same opportunity must land under the SAME
 * project on every device with no shared state to coordinate (ADR 0019). So the
 * project id is DERIVED by hashing owner + client + opportunity: any device that
 * knows those three computes the identical UUID.
 *
 * SHA-256 → first 16 bytes → an RFC-4122 UUID (version bits set to 5, the
 * name-based version, and the variant bits set), which is exactly what a
 * `uuid` Postgres column accepts. Case- and whitespace-folded so "Smith" and
 * " smith " group together, matching spaceKey().
 */

/** Fold identity text the same way spaceKey() does, so grouping is consistent. */
function fold(value) {
  return String(value ?? '').trim().toLowerCase();
}

function bytesToUuid(bytes) {
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
}

/**
 * The project UUID for (owner, client, opportunity). Async because
 * crypto.subtle.digest is; callers await it once per sync and pass the result
 * into the pure mapping.
 *
 * @param {string} ownerId  auth user id (the RLS owner)
 * @param {string} client
 * @param {string} opportunity
 * @returns {Promise<string>} a deterministic RFC-4122 UUID
 */
export async function deriveProjectId(ownerId, client, opportunity) {
  const material = `${fold(ownerId)}\n${fold(client)}\n${fold(opportunity)}`;
  const data = new TextEncoder().encode(material);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', data));
  const bytes = digest.slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50; // version 5 (name-based)
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC-4122 variant
  return bytesToUuid(bytes);
}
